import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEMANTIC, UI } from '../../styles/theme';
import API from '../../api';

const ACCENT = '#00ff88';
const BLUE   = '#00aaff';

const MODES = [
  {
    key: 'paper',
    icon: '🧪',
    title: 'Paper Trading',
    subtitle: 'Inizia senza rischi reali',
    color: ACCENT,
    badge: 'Attivo di default',
    badgeColor: ACCENT,
    features: [
      { icon: '💵', text: 'Usa denaro virtuale fornito da Alpaca' },
      { icon: '📊', text: 'Testa le strategie in condizioni di mercato reali' },
      { icon: '🔒', text: 'Nessun rischio finanziario effettivo' },
      { icon: '🚀', text: 'Operativo immediatamente, senza depositi' },
    ],
    description:
      'Il Paper Trading ti permette di operare con un portafoglio simulato da $100.000. ' +
      'Gli ordini vengono eseguiti a prezzi reali di mercato, ma senza usare denaro vero. ' +
      'È la scelta ideale per prendere confidenza con la piattaforma prima di investire.',
  },
  {
    key: 'real',
    icon: '💼',
    title: 'Real Trading',
    subtitle: 'Investi denaro reale',
    color: BLUE,
    badge: 'Attivabile dalle impostazioni',
    badgeColor: BLUE,
    features: [
      { icon: '📈', text: 'Rendimenti e perdite reali sul tuo conto' },
      { icon: '🏦', text: 'Fondi depositati sul tuo conto Alpaca' },
      { icon: '⚡', text: 'Esecuzione degli ordini sul mercato reale' },
      { icon: '🔍', text: 'Reportistica completa per la dichiarazione fiscale' },
    ],
    description:
      'Il Real Trading opera direttamente con i fondi presenti sul tuo conto Alpaca. ' +
      'Per attivarlo avrai bisogno di un account Alpaca verificato con fondi depositati. ' +
      'Potrai abilitarlo in qualsiasi momento dalle impostazioni del tuo profilo.',
  },
];

export default function TradingModeSelection() {
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');

  const handleContinue = async () => {
    setSaving(true);
    try {
      await API.put(`/users/${userId}/trading-mode`, { trading_mode: 'paper' });
    } catch (_) { /* non bloccante */ }
    setSaving(false);
    navigate('/kyc/autopilot');
  };

  return (
    <div style={{
      backgroundColor: SEMANTIC.backgroundApp,
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      padding: UI.spacing.lg,
    }}>
      <div style={{ maxWidth: '760px', margin: '0 auto', width: '100%' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto Logo" style={{ width: '120px' }} />
        </div>

        <div style={{
          backgroundColor: SEMANTIC.backgroundCard,
          borderRadius: UI.borderRadius.md,
          padding: UI.spacing.xl,
          boxShadow: UI.shadow.card,
        }}>
          <h2 style={{
            color: ACCENT,
            marginBottom: UI.spacing.sm,
            fontSize: UI.fontSize.title,
            fontWeight: UI.fontWeight.bold,
            textAlign: 'center',
          }}>
            Modalità di trading
          </h2>
          <p style={{
            color: SEMANTIC.textSecondary,
            marginBottom: UI.spacing.xl,
            textAlign: 'center',
            fontSize: UI.fontSize.md,
            lineHeight: 1.5,
          }}>
            Kuesto supporta due modalità di trading. Inizierai in Paper Trading e potrai attivare il Real Trading in qualsiasi momento dalle impostazioni.
          </p>

          {/* Card grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: UI.spacing.xl }}>
            {MODES.map(mode => (
              <div
                key={mode.key}
                style={{
                  border: `2px solid ${mode.key === 'paper' ? `${mode.color}66` : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: UI.borderRadius.md,
                  padding: '20px',
                  backgroundColor: mode.key === 'paper' ? `${mode.color}08` : SEMANTIC.backgroundApp,
                  position: 'relative',
                }}
              >
                {/* Badge */}
                <div style={{
                  position: 'absolute', top: 12, right: 12,
                  backgroundColor: `${mode.badgeColor}20`,
                  color: mode.badgeColor,
                  border: `1px solid ${mode.badgeColor}55`,
                  fontSize: 10, fontWeight: 700,
                  padding: '2px 8px', borderRadius: 20,
                  letterSpacing: 0.5,
                }}>
                  {mode.badge}
                </div>

                {/* Icon + title */}
                <div style={{ marginBottom: 12, paddingRight: 90 }}>
                  <span style={{ fontSize: 32 }}>{mode.icon}</span>
                  <div style={{
                    color: mode.key === 'paper' ? mode.color : SEMANTIC.textPrimary,
                    fontSize: 18, fontWeight: 700, marginTop: 8,
                  }}>
                    {mode.title}
                  </div>
                  <div style={{ color: SEMANTIC.textSecondary, fontSize: 12, marginTop: 2 }}>
                    {mode.subtitle}
                  </div>
                </div>

                {/* Description */}
                <p style={{
                  color: SEMANTIC.textSecondary,
                  fontSize: 12,
                  lineHeight: 1.6,
                  marginBottom: 14,
                }}>
                  {mode.description}
                </p>

                {/* Feature list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {mode.features.map((f, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 14 }}>{f.icon}</span>
                      <span style={{ fontSize: 12, color: SEMANTIC.textSecondary }}>{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Info note */}
          <div style={{
            backgroundColor: 'rgba(255,209,102,0.08)',
            border: '1px solid rgba(255,209,102,0.2)',
            borderRadius: UI.borderRadius.sm,
            padding: '10px 14px',
            marginBottom: UI.spacing.xl,
            fontSize: 12,
            color: SEMANTIC.textSecondary,
            lineHeight: 1.6,
          }}>
            💡 <strong style={{ color: '#ffd166' }}>Suggerimento:</strong> Se è la prima volta che usi Kuesto,
            il Paper Trading è perfetto per familiarizzare con i modelli algoritmici prima di investire capitale reale.
          </div>

          {/* Continue button */}
          <button
            onClick={handleContinue}
            disabled={saving}
            style={{
              width: '100%',
              padding: UI.spacing.md,
              backgroundColor: saving ? SEMANTIC.backgroundDisabled : ACCENT,
              color: saving ? SEMANTIC.textDisabled : '#000',
              border: 'none',
              borderRadius: UI.borderRadius.sm,
              fontSize: UI.fontSize.lg,
              fontWeight: UI.fontWeight.bold,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
            }}
          >
            Continua →
          </button>
        </div>
      </div>
    </div>
  );
}
